import { Component, Input, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep } from 'lodash';
import { CustomDataForProfileActions, ProfilePostActions } from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { debounceTime, filter, take, tap } from 'rxjs';
import { LoadRmmCustomNotificationWizardService } from '../../../../utility/load-rmm-custom-notification-wizard.service';

@UntilDestroy()
@Component({
  selector: 'mbs-alert-profile-notifications',
  templateUrl: './alert-profile-notifications.component.html'
})
export class AlertProfileNotificationSettingsAction {
  @Input() enabled = false;
  @Input() includeConnectWise = false;
  @Input() postActions: ProfilePostActions = null;
  @Input() unifiedPostActions: ProfilePostActions = null;
  public alertList: string[] = [];

  private data: ProfilePostActions;
  private readonly commonKey = 'general';
  private readonly excludedKeysList = ['pushNotification', 'pushRecipient', 'recipient'];

  constructor(private loadRmmCustomNotificationWizardService: LoadRmmCustomNotificationWizardService) {
    this.loadData();
  }

  ngOnInit(): void {
    this.loadRmmCustomNotificationWizardService.notificationsToSave
      .pipe(
        debounceTime(300),
        tap(() => (this.alertList = [])),
        filter(() => this.enabled),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        if (!value) return;

        this.alertList = Object.keys(value).flatMap((key) => (value[key]?.enabled && !this.excludedKeysList.includes(key) ? [key] : []));

        if (this.data)
          Object.keys(this.data).forEach((key) => {
            if (this.alertList.includes(key)) return;

            delete this.data[key];
          });

        this.inheritFromUnified();
      });
  }

  inheritFromUnified() {
    if (this.unifiedPostActions?.general) {
      this.alertList.forEach((key) => {
        this.data = this.data
          ? {
              ...this.data,
              [key]: cloneDeep(this.unifiedPostActions?.general)
            }
          : {
              [key]: cloneDeep(this.unifiedPostActions?.general)
            };
      });

      this.postActions = cloneDeep(this.data);
      this.loadRmmCustomNotificationWizardService.profileActionPayload.next({ ...this.data, [this.commonKey]: null });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data) {
      this.loadRmmCustomNotificationWizardService.profileActionPayload.next({ ...this.data, [this.commonKey]: null });
    }
    if (this.alertList && changes?.unifiedPostActions?.currentValue) {
      this.inheritFromUnified();
    }
  }

  onPostActionChanged(data: CustomDataForProfileActions) {
    if (!data?.type) return;

    this.data = {
      ...this.loadRmmCustomNotificationWizardService.profileActionPayload.getValue(),
      [this.commonKey]: null,
      [data.type]: { ...data.postActionPayload }
    };

    this.loadRmmCustomNotificationWizardService.profileActionPayload.next(cloneDeep(this.data));
  }

  loadData() {
    this.loadRmmCustomNotificationWizardService.profileActionPayload
      .pipe(
        filter((value) => !this.unifiedPostActions && value && !(this.commonKey in value && value[this.commonKey]?.recipient)),
        take(1)
      )
      .subscribe((postActions) => (this.postActions = postActions));
  }
}
